import { useState, useEffect, useCallback, useMemo } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * SOUND-689/690 — the cross-application pend list.
 *
 * Maps 1:1 to `GET /pends`, which is server-scoped on the pend's OWN companyId.
 * Until SOUND-689 there was no such endpoint at all: pends lived inside
 * `midApplications.pends[]`, so "what is outstanding across the portfolio" —
 * the question Ben's standup opens with — could not be asked.
 *
 * Params are serialised to a STRING for the dependency, not passed as an object.
 * A caller building a fresh filters object each render would otherwise refetch
 * every render; listing each field by hand is the bug SOUND-688 hit in
 * useMidApplications, where a filter silently never reached the API.
 */
export function usePends({ filters = {}, page = 1, limit = 200, sort = "createdAt" } = {}) {
  const [pends, setPends] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit, total: 0, totalPages: 0 });
  const [summary, setSummary] = useState({ total: 0, open: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const query = useMemo(
    () =>
      JSON.stringify(
        Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== "" && v != null)
        )
      ),
    [filters]
  );

  const fetchPends = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("sort", sort);
      for (const [k, v] of Object.entries(JSON.parse(query))) params.set(k, String(v));

      const res = await makeApiRequest(
        "get",
        `${apiRoutes.listPends}?${params.toString()}`,
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId
      );
      const body = res?.data || {};
      setPends(Array.isArray(body.data) ? body.data : []);
      setPagination(body.pagination || { page: 1, limit, total: 0, totalPages: 0 });
      setSummary(body.summary || { total: 0, open: 0, resolved: 0 });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [companyId, page, limit, sort, query, auth?.user?.id, auth?.user?.accessToken]);

  useEffect(() => {
    fetchPends();
  }, [fetchPends]);

  return { pends, pagination, summary, loading, error, refetch: fetchPends };
}

/**
 * The pend vocabulary, served by the API.
 *
 * NEVER hardcode this list — same rule as GET /tickets/request-types. A frontend
 * copy is exactly how 36 spellings of eight categories happened (SOUND-621).
 */
export function usePendCategories() {
  const [categories, setCategories] = useState([]);
  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  useEffect(() => {
    let alive = true;
    if (!companyId || !auth?.user) return undefined;
    makeApiRequest(
      "get",
      apiRoutes.pendCategories,
      { id: auth.user.id, accessToken: auth.user.accessToken },
      companyId
    )
      .then((res) => {
        if (alive) setCategories(res?.data?.data || []);
      })
      .catch(() => {
        // A failed vocabulary fetch must not blank the board. The list is used
        // for LABELS and a filter; the pends themselves render regardless.
        if (alive) setCategories([]);
      });
    return () => {
      alive = false;
    };
  }, [companyId, auth?.user]);

  return { categories };
}
